// =============================================
// serverBlobStore — client for the generic binary blob service (/api/blob)
// =============================================
// The sitewide way to persist binary files (Music audio/video, Photos, Notes
// attachments, …) to the SERVER the moment browser processing finishes, so
// nothing the user uploads lives only in the browser (see CLAUDE.md → File
// Storage Rules). Each service passes a `scope` (e.g. 'music', 'photos'); the
// owning DB row stores the returned `blob:<scope>/<acct>/<uuid>.<ext>` pointer.
//
// Docs use the parallel string-based src/lib/serverDocStore.ts (/api/dms/blob);
// this module is for raw binary Blobs.
// =============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

/** Is this a key the SERVER (or a remote URL) owns, vs a local idb:// blob? */
export function isServerBlobKey(key: string | null | undefined): boolean {
  return !!key && (key.startsWith('blob:') || key.startsWith('http://') || key.startsWith('https://'));
}

export interface PutBlobOpts {
  scope: string;                       // service that owns the blob (music, photos, …)
  accountOwnerId?: number | string | null;
  ext?: string;                        // file extension hint (mp3, jpg, …)
}

/**
 * Upload a binary Blob to the server blob store and return its `blob:` key, or
 * null if the server is unavailable / the user isn't logged in (caller then
 * falls back to a local IndexedDB store).
 */
export async function putServerBlob(blob: Blob, opts: PutBlobOpts): Promise<string | null> {
  const token = authToken();
  if (!token) return null;
  const params = new URLSearchParams({ scope: opts.scope });
  if (opts.accountOwnerId != null) params.set('account_owner_id', String(opts.accountOwnerId));
  if (opts.ext) params.set('ext', opts.ext);
  try {
    const res = await fetch(`${API_BASE}/blob?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': blob.type || 'application/octet-stream',
        Authorization: `Bearer ${token}`,
      },
      body: blob,
    });
    if (!res.ok) return null;
    const json = await res.json();
    return typeof json?.key === 'string' ? json.key : null;
  } catch {
    return null;
  }
}

/** Fetch a server/remote blob as a Blob, or null. */
export async function getServerBlob(key: string): Promise<Blob | null> {
  const token = authToken();
  try {
    if (key.startsWith('http://') || key.startsWith('https://')) {
      const res = await fetch(key);
      return res.ok ? await res.blob() : null;
    }
    const url = `${API_BASE}/blob?key=${encodeURIComponent(key)}` +
      (token ? `&token=${encodeURIComponent(token)}` : '');
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    return res.ok ? await res.blob() : null;
  } catch {
    return null;
  }
}

/** Fetch a server/remote blob and wrap it in an object URL (caller revokes it). */
export async function getServerBlobUrl(key: string): Promise<string | null> {
  const blob = await getServerBlob(key);
  return blob ? URL.createObjectURL(blob) : null;
}

/** Best-effort delete (never block a delete on this). */
export async function deleteServerBlob(key: string): Promise<void> {
  const token = authToken();
  if (!token || !key.startsWith('blob:')) return;
  try {
    await fetch(`${API_BASE}/blob?key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch { /* ignore */ }
}
